import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import Select from 'components/Form/Select'
import Switch from 'components/Form/Switch'
import { Alert } from 'features/alerts/types/alert'
import { useGetPrioritiesForSelect } from 'features/globalSetup'
import { DomainBidderAlert } from 'features/globalSetup/api/types/domainBidderAlert'
import { Bidder } from 'features/globalSetup/types/bidder'
import {
  DomainBidderPriority,
  WorkspaceBidderPriority,
} from 'features/globalSetup/types/bidderPriority'
import { DomainBidderAlerts } from 'features/inventory/domain/forms/PrebidStackForm/sections/BiddersSection/components/DomainBidderAlerts'
import { useAbility } from 'providers/casl'
import { Schema } from '../../../form/schema'

type Props = {
  alerts: Alert<DomainBidderAlert>[]
  s2sAvailable: Bidder['s2sAvailable']
  workspaceBidderPriority: WorkspaceBidderPriority
}

export const DomainBidderFields = ({
  alerts,
  s2sAvailable,
  workspaceBidderPriority,
}: Props): JSX.Element => {
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'prebid.bidders.edit.bidParamsAndConnection.domainSection',
  })
  const ability = useAbility()
  const { control } = useFormContext<Schema>()

  const priorities = useGetPrioritiesForSelect({ variant: 'domainBidder', workspaceBidderPriority })

  const csAlerts = alerts.filter((alert: Alert<DomainBidderAlert>): boolean =>
    alert.name.startsWith('cs'),
  )
  const s2sAlerts = alerts.filter((alert: Alert<DomainBidderAlert>): boolean =>
    alert.name.startsWith('s2s'),
  )

  return (
    <>
      <Select<Schema, DomainBidderPriority>
        control={control}
        disabled={!ability.can('update', 'GlobalSetupField-prebid-bidders-bid-params-priority')}
        label={t('priority.label')}
        name='priority'
        options={priorities}
        tooltip={{
          content: t('priority.tooltip'),
        }}
      />

      <FlexBox axis='x'>
        <Switch
          control={control}
          disabled={!ability.can('update', 'GlobalSetupField-prebid-bidders-bid-params-cs')}
          label={t('csEnabled.label')}
          labelPlacement='start'
          name='csEnabled'
          tooltip={{
            content: t('csEnabled.tooltip'),
          }}
        />

        {csAlerts.length > 0 && <DomainBidderAlerts alerts={csAlerts} />}
      </FlexBox>

      {s2sAvailable && (
        <FlexBox axis='x'>
          <Switch
            control={control}
            disabled={!ability.can('update', 'GlobalSetupField-prebid-bidders-bid-params-s2s')}
            label={t('s2sEnabled.label')}
            labelPlacement='start'
            name='s2sEnabled'
            tooltip={{
              content: t('s2sEnabled.tooltip'),
            }}
          />

          {s2sAlerts.length > 0 && <DomainBidderAlerts alerts={s2sAlerts} />}
        </FlexBox>
      )}
    </>
  )
}
